import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { HttpClient } from '@angular/common/http';
import { catchError, of } from 'rxjs';

import { ContextService } from '../../core/context.service';
import { LanguageService } from '../../core/i18n/language.service';
import { Button } from '../../shared/components/button/button';
import { Modal } from '../../shared/components/modal/modal';
import { Checkbox } from '../../shared/components/forms/checkbox';
import { Select, SelectOption } from '../../shared/components/forms/select';
import { TextInput } from '../../shared/components/forms/text-input';
import { GridPage } from '../../shared/components/grid/grid-page';
import { ContentDeliveryService } from '../providers/services/content-delivery.service';
import { FeedService } from '../../shared/services/feed.service';
import { Feed } from '../../shared/models/feed.model';
import { ToastService } from '../../shared/services/toast-service';
import { PreviewError, PreviewService } from './services/preview.service';

type ContentTab = 'preview' | 'data' | 'bindings';

/** How long to wait after the last edit (file/feed/stop change) before firing a
 *  render — the endpoint round-trips to the render-service, so we don't want a
 *  request per keystroke in the stop-id field. */
const RENDER_DEBOUNCE_MS = 400;

/** Starter departure-board template shown before the author loads their own
 *  file. It exercises the token/each/if syntax the render engine supports, so
 *  the first preview is meaningful with any feed+stop that has cached data.
 *  Lives as an asset (not an inline string) because its SVG source is device
 *  render content, not app UI. */
const SAMPLE_SVG_URL = 'assets/content/sample-board.svg';

/** A single placeholder-token reference row shown in the BINDINGS tab. The
 *  descriptions are translated; tokens are literal and stay as-is. */
interface Binding {
  token: string;
  descKey: string;
}

/** The token reference the render engine understands. Tokens are literal;
 *  `descKey` points at a `content.bindings.*` i18n key. Kept in sync with the
 *  SVG template spec the render-service implements. */
const BINDINGS: Binding[] = [
  { token: '{{stop.name}}', descKey: 'stopName' },
  { token: '{{stop.id}}', descKey: 'stopId' },
  { token: '{{feed.name}}', descKey: 'feedName' },
  { token: '{{now.time}}  {{now.date}}', descKey: 'now' },
  { token: '{{updated}}', descKey: 'updated' },
  { token: '{{message.line1}}  {{message.line2}}', descKey: 'message' },
  { token: '<!--each departures limit=6 step=120-->…<!--/each-->', descKey: 'each' },
  { token: '{{line}}  {{destination}}  {{via}}  {{quay}}', descKey: 'row' },
  { token: '{{due}}', descKey: 'due' },
  { token: '{{aimed}}  {{expected}}  {{status}}', descKey: 'times' },
  { token: '<!--if cancelled-->…<!--else-->…<!--/if-->', descKey: 'if' },
];

/** A candidate display in the Assign modal. Stubbed until the assign endpoint
 *  lands — the list is illustrative, the confirm only toasts. */
interface DisplayChoice {
  id: string;
  name: string;
  metaKey: string;
  online: boolean;
  checked: boolean;
}

/**
 * Content — SVG app previewer (/:org/content).
 *
 * A tenant author picks an SVG template file, a feed, and a stop, and sees the
 * template rendered against that stop's live cached departures. Rendering is
 * server-side: the SVG + feedId + stopId go to core's POST /api/v1/preview,
 * which proxies the internal render-service and returns a PNG — so the preview
 * is pixel-accurate to what a device shows, not a browser approximation.
 *
 * Three tabs over the render:
 *   PREVIEW  — the returned PNG on a white screen surface, with drag-drop file
 *              replacement.
 *   SIRI DATA — the raw content-delivery payload feeding the render.
 *   BINDINGS  — the placeholder tokens the engine substitutes.
 *
 * Save-as-content and assign-to-display are stubbed (toast only) until their
 * endpoints exist.
 */
@Component({
  selector: 'app-content',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    TranslocoDirective,
    GridPage,
    Button,
    Modal,
    Checkbox,
    Select,
    TextInput,
  ],
  templateUrl: './content.component.html',
})
export class ContentComponent {
  private readonly feedService = inject(FeedService);
  private readonly contentDelivery = inject(ContentDeliveryService);
  private readonly preview = inject(PreviewService);
  private readonly context = inject(ContextService);
  private readonly toast = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly language = inject(LanguageService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  private readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');

  // --- Template + selection state ---------------------------------------
  /** Empty until the sample template asset loads; the render effect no-ops while
   *  empty, so the first render fires once it arrives. */
  protected readonly svgText = signal('');
  protected readonly fileName = signal('');
  protected readonly feedId = signal<string | null>(null);
  protected readonly stopId = signal('');
  protected readonly tab = signal<ContentTab>('preview');

  // --- Preview render state ---------------------------------------------
  /** Object URL of the last successful PNG, shown in the frame. Revoked on
   *  replacement so blobs don't leak. */
  protected readonly previewUrl = signal<SafeUrl | null>(null);
  private lastObjectUrl: string | null = null;
  protected readonly rendering = signal(false);
  protected readonly errorText = signal('');

  // --- Feeds -------------------------------------------------------------
  /** Feeds grouped by provider name, from the tenant-facing listing. */
  private readonly feedsByProvider = toSignal(
    this.feedService.getAll().pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(() => of(null)),
    ),
    { initialValue: undefined },
  );

  /** Flat list of every feed the tenant owns, across providers. */
  protected readonly feeds = computed<Feed[]>(() => {
    const grouped = this.feedsByProvider();
    if (!grouped?.data) return [];
    return Object.values(grouped.data).flat();
  });

  /** `provider · feed name` options for the feed dropdown. Provider name is the
   *  group key; we prefix it so a tenant with feeds across providers can tell
   *  them apart in one flat list. */
  protected readonly feedOptions = computed<SelectOption[]>(() => {
    const grouped = this.feedsByProvider()?.data;
    if (!grouped) return [];
    return Object.entries(grouped).flatMap(([provider, feeds]) =>
      feeds.map((feed) => ({ value: feed.id, label: `${provider} · ${feed.name}` })),
    );
  });

  protected readonly selectedFeed = computed<Feed | null>(
    () => this.feeds().find((feed) => feed.id === this.feedId()) ?? null,
  );

  // --- Assign modal (stub) ----------------------------------------------
  protected readonly assignOpen = signal(false);
  protected readonly displays = signal<DisplayChoice[]>([]);

  // --- Derived flags -----------------------------------------------------
  protected readonly hasStop = computed(() => this.stopId().trim().length > 0);
  protected readonly canRender = computed(() => !!this.feedId() && this.hasStop());
  /** Save/Assign need a rendered app + a target, and are gated on a clean render. */
  protected readonly cannotSave = computed(() => !this.canRender() || !this.previewUrl());

  protected readonly appName = computed(() =>
    (this.fileName() || this.transloco.translate('content.sampleFileName')).replace(/\.svg$/i, ''),
  );

  protected readonly bindings = BINDINGS;

  // --- SIRI DATA tab -----------------------------------------------------
  /** Raw content-delivery payload for the current feed+stop, pretty-printed.
   *  Loaded lazily when the tab is first shown and on refresh. */
  protected readonly rawJson = signal('');
  protected readonly rawLoading = signal(false);
  protected readonly departureCount = signal<number | null>(null);

  protected readonly tenantId = computed(() => this.context.getOrganization()?.id ?? '');

  constructor() {
    // Load the starter template so the first preview is meaningful before the
    // author picks their own file. A failure here just leaves the editor empty
    // with the "choose a file" prompt — not fatal.
    this.http
      .get(SAMPLE_SVG_URL, { responseType: 'text' })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of('')),
      )
      .subscribe((svg) => {
        if (svg && !this.fileName()) {
          this.svgText.set(svg);
        }
      });

    // Auto-select the first feed once the listing arrives, so a tenant with a
    // single feed doesn't have to touch the dropdown.
    effect(() => {
      const feeds = this.feeds();
      if (feeds.length && !this.feedId()) {
        this.feedId.set(feeds[0].id);
      }
    });

    // Re-render whenever the template, feed, or stop changes. Debounced via a
    // timer we reset on each change — cheaper than an rxjs pipeline for signals.
    effect((onCleanup) => {
      // Read the deps so the effect re-runs on any of them.
      const svg = this.svgText();
      const feedId = this.feedId();
      const stop = this.stopId().trim();
      if (!svg || !feedId || !stop) {
        this.clearPreview();
        return;
      }
      const handle = setTimeout(() => this.runRender(svg, feedId, stop), RENDER_DEBOUNCE_MS);
      onCleanup(() => clearTimeout(handle));
    });

    this.destroyRef.onDestroy(() => this.revokeObjectUrl());
  }

  // --- File handling -----------------------------------------------------
  protected pickFile(): void {
    this.fileInput().nativeElement.click();
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.loadFile(input.files?.[0]);
    input.value = '';
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    this.loadFile(event.dataTransfer?.files?.[0]);
  }

  private loadFile(file: File | undefined): void {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      this.svgText.set(String(reader.result ?? ''));
      this.fileName.set(file.name);
      this.tab.set('preview');
    };
    reader.readAsText(file);
  }

  // --- Rendering ---------------------------------------------------------
  private runRender(svg: string, feedId: string, stopId: string): void {
    this.rendering.set(true);
    this.errorText.set('');
    this.preview
      .render({ svg, feedId, stopId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => {
          this.setPreviewBlob(blob);
          this.rendering.set(false);
        },
        error: (error: PreviewError) => {
          this.rendering.set(false);
          this.errorText.set(this.messageForError(error));
        },
      });
  }

  private setPreviewBlob(blob: Blob): void {
    this.revokeObjectUrl();
    this.lastObjectUrl = URL.createObjectURL(blob);
    this.previewUrl.set(this.sanitizer.bypassSecurityTrustUrl(this.lastObjectUrl));
  }

  private clearPreview(): void {
    this.revokeObjectUrl();
    this.previewUrl.set(null);
    this.errorText.set('');
  }

  private revokeObjectUrl(): void {
    if (this.lastObjectUrl) {
      URL.revokeObjectURL(this.lastObjectUrl);
      this.lastObjectUrl = null;
    }
  }

  /** Translates a PreviewError to a user-facing line. The server's own reason
   *  (422/502 body) is appended where present — it's the render engine's
   *  diagnostic, more specific than any generic copy. */
  private messageForError(error: PreviewError): string {
    this.language.current();
    const base = this.transloco.translate(`content.error.${error.kind}`);
    return error.detail ? `${base} ${error.detail}` : base;
  }

  // --- Tabs --------------------------------------------------------------
  protected showTab(tab: ContentTab): void {
    this.tab.set(tab);
    if (tab === 'data') this.loadRawData();
  }

  /** Refresh pulls fresh cached departures without any input change, so it can't
   *  ride the render effect (which only fires when svg/feed/stop change) — it
   *  calls the render path directly. Also refetches the raw payload when the
   *  data tab is open. */
  protected refresh(): void {
    const feedId = this.feedId();
    const stop = this.stopId().trim();
    if (feedId && stop) this.runRender(this.svgText(), feedId, stop);
    if (this.tab() === 'data') this.loadRawData();
  }

  private loadRawData(): void {
    const feedId = this.feedId();
    const stop = this.stopId().trim();
    const tenantId = this.tenantId();
    if (!feedId || !stop || !tenantId) {
      this.rawJson.set(this.transloco.translate('content.data.noStop'));
      this.departureCount.set(null);
      return;
    }
    this.rawLoading.set(true);
    this.contentDelivery
      .getStopContent(tenantId, feedId, stop)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of(null)),
      )
      .subscribe((response) => {
        this.rawLoading.set(false);
        if (!response?.data) {
          // 404 = nothing published for this stop yet, not an error.
          this.rawJson.set(this.transloco.translate('content.data.empty'));
          this.departureCount.set(0);
          return;
        }
        this.rawJson.set(JSON.stringify(response.data, null, 2));
        this.departureCount.set(response.data.content?.departures?.length ?? 0);
      });
  }

  // --- Save / Assign (stubbed) ------------------------------------------
  protected saveContent(): void {
    this.toast.show(
      this.transloco.translate('content.toast.savedMessage', { name: this.appName() }),
      'success',
      { title: this.transloco.translate('content.toast.savedTitle') },
    );
  }

  protected openAssign(): void {
    // Illustrative candidate displays until the assign endpoint exists.
    this.displays.set([
      { id: 'd1', name: this.transloco.translate('content.assign.sampleDisplay1'), metaKey: 'online', online: true, checked: false },
      { id: 'd2', name: this.transloco.translate('content.assign.sampleDisplay2'), metaKey: 'offline', online: false, checked: false },
      { id: 'd3', name: this.transloco.translate('content.assign.sampleDisplay3'), metaKey: 'online', online: true, checked: false },
    ]);
    this.assignOpen.set(true);
  }

  protected toggleDisplay(id: string, checked: boolean): void {
    this.displays.update((list) => list.map((d) => (d.id === id ? { ...d, checked } : d)));
  }

  protected readonly checkedCount = computed(() => this.displays().filter((d) => d.checked).length);

  protected confirmAssign(): void {
    const count = this.checkedCount();
    this.assignOpen.set(false);
    this.toast.show(
      this.transloco.translate('content.toast.assignedMessage', { name: this.appName(), count }),
      'success',
      { title: this.transloco.translate('content.toast.assignedTitle') },
    );
  }
}
